import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { companyService } from '../../services/companyService'

const TalentRequest = () => {
  const navigate = useNavigate()
  const { state } = useLocation()
  const [selectedResources, setSelectedResources] = useState(state?.selectedResources || [])
  const [jobDescription, setJobDescription] = useState('')
  const [duration, setDuration] = useState('')
  const [budget, setBudget] = useState('')
  const [availabilities, setAvailabilities] = useState([])
  const [newDate, setNewDate] = useState('')
  const [newTime, setNewTime] = useState('')
  const [interviewRounds, setInterviewRounds] = useState(1)
  const [roundTypes, setRoundTypes] = useState([]) // e.g. ['L1','L2','HR']
  const [rounds, setRounds] = useState([]) // Array of { name, duration }
  const [newRoundName, setNewRoundName] = useState('')
  const [newRoundDuration, setNewRoundDuration] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const toggleRoundType = (type) => {
    setRoundTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type])
  }

  const addRound = () => {
    if (!newRoundName.trim()) return
    setRounds(prev => {
      const next = [...prev, { name: newRoundName.trim(), duration: newRoundDuration.trim() }]
      setInterviewRounds(next.length)
      return next
    })
    setNewRoundName('')
    setNewRoundDuration('')
  }

  const removeRound = (idx) => {
    setRounds(prev => {
      const next = prev.filter((_, i) => i !== idx)
      setInterviewRounds(next.length || 1)
      return next
    })
  }

  const addAvailability = () => {
    if (!newDate || !newTime) return toast.error('Please select date and time')
    setAvailabilities(prev => [...prev, { date: newDate, time: newTime }])
    setNewTime('')
  }

  const removeAvailability = (idx) => {
    setAvailabilities(prev => prev.filter((_, i) => i !== idx))
  }

  const removeResource = (id) => {
    setSelectedResources(prev => prev.filter(r => r.id !== id))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!jobDescription.trim()) return toast.error('Job description is required')
    if (!duration.trim()) return toast.error('Duration is required')
    if (!budget || isNaN(Number(budget))) {
      return toast.error('Valid budget is required')
    }

    if (rounds.length === 0) return toast.error('At least one interview round is required')
    // Availability is optional? User didn't say it's required, but "add fields". I'll default to optional or warn if empty? 
    // Let's make it optional for now or just check if they want to add it.



    setLoading(true)
    try {
      const payload = {
        description: jobDescription,
        duration,
        budget: String(budget),
        interview_rounds: Number(interviewRounds),
        interview_structure: rounds.map(r => ({
          round_name: r.name,
          duration: r.duration
        })),
        preferred_availability: availabilities.map(a => {
          // Format date from YYYY-MM-DD to DD/MM/YYYY
          const [year, month, day] = a.date.split('-')
          const formattedDate = `${day}/${month}/${year}`

          // Format time from HH:mm to HH:mmAM/PM
          const [hours, minutes] = a.time.split(':')
          const h = parseInt(hours, 10)
          const ampm = h >= 12 ? 'PM' : 'AM'
          const h12 = h % 12 || 12
          const formattedTime = `${h12}:${minutes}${ampm}`

          return {
            date: formattedDate,
            time: formattedTime
          }
        }),
        resource_ids: selectedResources.map(r => r.id)
      }

      await companyService.createResourceRequest(payload)
      toast.success('Request submitted successfully')
      sessionStorage.removeItem('selectedBenchResourceIds')
      navigate('/dashboard/company/jobs')
    } catch (err) {
      console.error('Request failed', err)
      // Extract specific validation message if available
      const validationMsg = err.response?.data?.detail?.[0]?.msg
      const serverMsg = err.response?.data?.message
      toast.error(validationMsg || serverMsg || err?.message || 'Failed to submit request')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-full max-w-6xl mx-auto px-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Request Talent</h1>
        <p className="text-sm text-secondary-600">Create a request with job details and interview rounds.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 bg-white p-6 rounded-xl border border-gray-200">

        {selectedResources.length > 0 && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
            <h3 className="text-md font-semibold text-blue-900 mb-2">Selected Bench Resources ({selectedResources.length})</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {selectedResources.map(resource => (
                <div key={resource.id} className="relative group flex items-center gap-3 bg-white p-2 pr-8 rounded border border-blue-100 shadow-sm transition-all hover:shadow-md">
                  {resource.image ? (
                    <img src={resource.image} alt={resource.name} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                      {resource.name?.charAt(0)}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{resource.name}</p>
                    <p className="text-xs text-slate-500 truncate">{resource.designation}</p>
                  </div>
                  <div className="ml-auto text-xs font-semibold text-success-600 whitespace-nowrap">
                    ${resource.cost_per_hour}/hr
                  </div>
                  <button
                    type="button"
                    onClick={() => removeResource(resource.id)}
                    className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-red-200"
                    title="Remove resource"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-1 h-full">
            <label className="label">Job Description</label>
            <textarea value={jobDescription} onChange={(e) => setJobDescription(e.target.value)} className="input mt-1 w-full h-full min-h-[420px]" placeholder="Describe the role, responsibilities, and expectations..." />
          </div>

          <div className="space-y-4">
            <div>
              <label className="label">Budget ($/hr)</label>
              <input
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                className="input mt-1 w-full"
                placeholder="e.g. 50"
                type="number"
              />
            </div>


            <div>
              <label className="label">Duration</label>
              <input value={duration} onChange={(e) => setDuration(e.target.value)} className="input mt-1 w-full" placeholder="e.g. 3 months" />
            </div>

            <div>
              <label className="label">Interview Rounds</label>

              <div className="mt-3 grid grid-cols-1 gap-2">
                <div className="flex items-center gap-2 mb-4">
                  <input value={newRoundName} onChange={(e) => setNewRoundName(e.target.value)} placeholder="Round name (e.g. L1)" className="input flex-1" />
                  <input value={newRoundDuration} onChange={(e) => setNewRoundDuration(e.target.value)} placeholder="Duration (e.g. 30 mins)" className="input w-36" />
                  <button type="button" onClick={addRound} className="px-3 py-1 bg-primary-600 text-white rounded-md">Add Round</button>
                </div>

                {rounds.length > 0 && (
                  <div className="space-y-2">
                    {rounds.map((r, idx) => (
                      <div key={idx} className="flex items-center justify-between gap-3 p-2 border rounded-md">
                        <div>
                          <div className="text-sm font-semibold">{r.name}</div>
                          <div className="text-xs text-secondary-500">{r.duration}</div>
                        </div>
                        <div>
                          <button type="button" onClick={() => removeRound(idx)} className="text-sm text-red-600">Remove</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}



              </div>
            </div>

            <div>
              <label className="label">Interview Availability</label>
              <div className="mt-3 grid grid-cols-1 gap-2">
                <div className="flex flex-wrap items-end gap-2 mb-4">
                  <div className="w-[140px]">
                    <span className="text-xs text-secondary-500 block mb-1">Date</span>
                    <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="input w-full" />
                  </div>
                  <div className="w-[110px]">
                    <span className="text-xs text-secondary-500 block mb-1">Time</span>
                    <input type="time" value={newTime} onChange={(e) => setNewTime(e.target.value)} className="input w-full" />
                  </div>
                  <button type="button" onClick={addAvailability} className="px-3 py-2 bg-primary-600 text-white rounded-md self-end mb-[1px]">Add</button>
                </div>

                {availabilities.length > 0 && (
                  <div className="space-y-2 border-t pt-2">
                    {availabilities.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between gap-3 p-2 bg-gray-50 border rounded-md">
                        <div className="text-sm">
                          <span className="font-semibold">{item.date}</span>: <span className="text-secondary-600">{item.time}</span>
                        </div>
                        <button type="button" onClick={() => removeAvailability(idx)} className="text-xs text-red-600 hover:text-red-800">
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>



        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={() => navigate('/dashboard/company/talent')} className="px-4 py-2 border rounded-md">Cancel</button>
          <button type="submit" disabled={loading} className="px-6 py-2 bg-primary-600 text-white rounded-md">{loading ? 'Submitting...' : 'Submit Request'}</button>
        </div>
      </form>
    </div>
  )
}

export default TalentRequest
